import { Injectable, inject } from '@angular/core';
import { IFilterService, IPromise, IQService, ITimeoutService } from 'angular';

import { CalendarService } from '../../calendar/calendar.service';
import { HelpersService } from '../../helpers/helpers.service';
import { ValidateService } from '../../validate/validate.service';
import { FILTER_PORT, Q_PORT, TIMEOUT_PORT } from '../../utils';

/**
 * ReportsGeneratorService (Angular 18)
 *
 * Ported 1:1 from apps/b2b-ui/common/services/reports/generator/generator.service.js
 * (legacy AngularJS name 'ReportsGeneratorService'). Builds the printable HTML
 * markup (thead/tbody/tfoot) for the `full` and `summary` report layouts.
 *
 * Internal sub-service of ReportsService (the only consumer); reached via
 * inject() and NOT downgrade-registered. Dependency mapping:
 *  - HelpersService / ValidateService / CalendarService → Angular 18 (inject).
 *  - `$q`/`$timeout`/`$filter` → Q_PORT / TIMEOUT_PORT / FILTER_PORT (shared
 *    fundamental ports in core/utils, wired in core.module.ts).
 *  - `angular.forEach` → native iteration. Wave 3 #16.
 */
@Injectable({ providedIn: 'root' })
export class ReportsGeneratorService {
  private helpersService = inject(HelpersService);
  private validateService = inject(ValidateService);
  private calendarService = inject(CalendarService);
  private $q = inject<IQService>(Q_PORT);
  private $timeout = inject<ITimeoutService>(TIMEOUT_PORT);
  private $filter = inject<IFilterService>(FILTER_PORT);

  type = '';
  markup = '';
  progress: any = 0;
  partName = 'generating';
  numericColumns: Record<string, boolean> = {};

  init(config: any): void {
    if (config.type) {
      this.type = config.type.toLowerCase();
    }

    if (config.progress) {
      this.progress = config.progress;
    }

    this.markup = '';
  }

  generate(report: any): IPromise<string> {
    const q = this.$q.defer<string>();

    if (this.type) {
      (this as any)[this.type](report).then((markup: string) => {
        this.markup = markup;
        q.resolve(this.markup);
      });
    } else {
      // Must return a promise
      this.markup = '';
      this.$timeout(() => {
        q.resolve(this.markup);
        return true;
      }, 30);
    }

    return q.promise;
  }

  isNumeric(value: any, field?: string): boolean {
    let r = this.validateService.validators.number(value);

    if (r && !this.numericColumns[field as string]) {
      // Only set to true if it is possible
      this.numericColumns[field as string] = r;
    } else if (this.numericColumns[field as string]) {
      r = true;
    }

    return r;
  }

  isUTCDate(date: any): boolean {
    return this.validateService.validators.utcDate(date);
  }

  getDisplayDate(date: any): any {
    let _date = date;
    if (this.isUTCDate(date)) {
      _date = this.$filter('date')(this.calendarService.parseDate(date), this.helpersService.dateFormats.sortDateTime);
    }
    return _date;
  }

  thead(group: any, columns: any[]): string {
    let j, jj;
    let tmp;
    let r = '';

    r +=
      '<thead> \
                <tr>';
    for (j = 0, jj = columns.length; j < jj; j++) {
      r += '<th';

      tmp = '';

      if (group.name === columns[j].field) {
        tmp += 'reports-print-summary-hide-column ';
      }

      if (this.numericColumns[columns[j].field]) {
        tmp += 'reports-print-subtotal-num';
      }

      if (tmp.length) {
        tmp = ' class="' + tmp + '"';
      }

      r +=
        tmp +
        '> \
                  <span>' +
        columns[j].displayName +
        '</span> \
                </th>';
    }

    r +=
      '</tr> \
            </thead>';

    return r;
  }

  tbody(group: any, columns: any[], type: string): string {
    let tmp;
    let c = 0;
    let r = '';

    r += '<tbody>';

    (group.values || []).forEach((row: any) => {
      let i, ii;

      if (row) {
        r += '<tr>';

        for (i = 0, ii = columns.length; i < ii; i++) {
          tmp = '';

          r += '<td';

          if (type === 'full') {
            if (this.isNumeric(row[columns[i].field], columns[i].field)) {
              tmp += 'reports-print-subtotal-num ';
            }

            if (c > 0) {
              tmp += 'reports-print-line';
            }
          } else if (type === 'summary') {
            if (this.isNumeric(row[columns[i].field], columns[i].field)) {
              tmp += 'reports-print-subtotal-num ';
            }

            if (columns[i].field !== 'undefined' && c > 0 && i > 0) {
              tmp += 'reports-print-line ';
            }

            if (row.subtotal && row.fields.indexOf(columns[i].field) === -1) {
              tmp += 'reports-print-subtotal ';
            }
          }

          if (tmp.length) {
            tmp = ' class="' + tmp + '"';
          }

          r += tmp + '>';

          if (group.value !== row[columns[i].field] || row.subtotal) {
            switch (columns[i].type) {
              case 'date':
                r += '<span>' + this.getDisplayDate(row[columns[i].field]) + '</span>';
                break;
              case 'currency':
                if (row[columns[i].field]) {
                  r += '<span>' + this.$filter('currency')(row[columns[i].field]) + '</span>';
                }
                break;
              default:
                r += '<span>' + row[columns[i].field] + '</span>';
            }
          }
        }

        r +=
          '</td> \
                </tr>';

        c++;
      }
    });

    r +=
      '<tr> \
                <td class="reports-print-summary-divider" colspan="' +
      columns.length +
      '"></td> \
              </tr> \
            </tbody>';

    return r;
  }

  tfoot(group: any, columns: any[], totals: any, type: string): string {
    let i, ii;
    let r = '';

    r += '<tfoot>';

    if (totals) {
      r +=
        '<tr> \
                    <td colspan="' +
        columns.length +
        '" class="reports-print-grand-total-spacer">&nbsp;</td> \
                  </tr> \
                  <tr> \
                    <td class="reports-print-grand-total"' +
        (type === 'summary' ? ' colspan="' + (columns.length - group.highlightWidth) + '"' : '') +
        '>Grand Total</td>';
      for (i = 0, ii = columns.length; i < ii; i++) {
        if (totals[columns[i].field]) {
          r += '<td class="reports-print-grand-total';

          if (this.isNumeric(totals[columns[i].field])) {
            r += ' reports-print-subtotal-num';
          }

          r += '">';

          switch (columns[i].type) {
            case 'date':
              r += '<span>' + this.getDisplayDate(totals[columns[i].field]) + '</span>';
              break;
            case 'currency':
              if (totals[columns[i].field]) {
                r += '<span>' + this.$filter('currency')(totals[columns[i].field]) + '</span>';
              }
              break;
            default:
              r += '<span>' + totals[columns[i].field] + '</span>';
          }

          r += '</td>';
        }
      }

      r += '</tr>';
    }

    r +=
      '<tr> \
                <td colspan="' +
      columns.length +
      '" class="reports-print-grand-total-spacer">&nbsp;</td> \
              </tr>';

    if (group.totalRecords) {
      r +=
        '<tr> \
                  <td colspan="' +
        columns.length +
        '" class="reports-print-grand-total"> \
                    <span>Total Records: ' +
        group.totalRecords +
        '</span> \
                  </td> \
                </tr>';
    }

    r += '</tfoot>';

    return r;
  }

  emptyReport(group: any, columns: any[]): string {
    let i, ii;
    let tmp;
    let r = '';

    r +=
      '<table> \
                <thead> \
                <tr>';

    for (i = 0, ii = columns.length; i < ii; i++) {
      r += '<th';
      tmp = '';

      if (group.name === columns[i].field) {
        tmp += 'reports-print-summary-hide-column ';
      }

      if (this.numericColumns[columns[i].field]) {
        tmp += 'reports-print-subtotal-num';
      }

      if (tmp.length) {
        tmp = ' class="' + tmp + '"';
      }

      r +=
        tmp +
        '> \
                <span>' +
        columns[i].displayName +
        '</span> \
              </th>';
    }

    r +=
      '</tr> \
            </thead> \
            <tbody> \
              <tr> \
                <td class="text-center" colspan="' +
      columns.length +
      '"> No items found. </td> \
              </tr> \
            </tbody> \
            <tfoot> \
              <tr> \
                <td colspan="' +
      columns.length +
      '" class="reports-print-grand-total-spacer">&nbsp;</td> \
              </tr> \
              <tr> \
                <td colspan="' +
      columns.length +
      '" class="reports-print-grand-total"> \
                  <span>Total Records: 0</span> \
                </td> \
                </tr> \
                </tfoot> \
              </table>';

    return r;
  }

  full(report: any): IPromise<string> {
    let i = 0;
    let r = '';
    let thead = '';
    let tbody = '';
    let tfoot = '';
    const q = this.$q.defer<string>();
    const keys = Object.keys(report.data);

    this.progress.setValue(this.partName, i);

    Object.keys(report.data).forEach((key) => {
      const group = report.data[key];

      this.$timeout(() => {
        r += '<div class="reports-print-summary full">';

        if (group.values instanceof Array && group.values.length) {
          r += '<table>';

          tbody = this.tbody(group, report.grid.columns, 'full');

          // thead is defined after tbody so numericColumns are populated for the headings
          thead = this.thead(group, report.grid.columns);

          // Last key
          if (key + '' === keys[keys.length - 1]) {
            // tfoot is defined after tbody so numericColumns are populated for the headings
            tfoot = this.tfoot(group, report.grid.columns, report.totals, 'full');
          }

          // End table
          r +=
            thead +
            tbody +
            tfoot +
            '</table> \
                </div>';
        } else {
          r += this.emptyReport(group, report.grid.columns);
          r += '</div>';
        }

        i++;

        this.progress.setValue(this.partName, Math.abs(i / keys.length) * 100);

        if (keys.length === i) {
          q.resolve(r);
        }

        return true;
      }, 30);
    });

    return q.promise;
  }

  summary(report: any): IPromise<string> {
    let i = 0;
    let r = '';
    let thead = '';
    let tbody = '';
    let tfoot = '';
    const q = this.$q.defer<string>();
    const keys = Object.keys(report.data);

    this.progress.setValue(this.partName, i);

    Object.keys(report.data).forEach((key) => {
      const group = report.data[key];

      this.$timeout(() => {
        r +=
          '<div class="reports-print-summary"> \
                <h4>' +
          group.name +
          ': <b>' +
          group.value +
          '</b></h4>';

        if (group.values instanceof Array && group.values.length) {
          r += '<table>';

          tbody = this.tbody(group, report.grid.columns, 'summary');
          // thead is defined after tbody so numericColumns are populated for the headings
          thead = this.thead(group, report.grid.columns);

          // Last key
          if (key + '' === keys[keys.length - 1]) {
            // tfoot is defined after tbody so numericColumns are populated for the headings
            tfoot = this.tfoot(group, report.grid.columns, report.totals, 'summary');
          }

          // End table
          r +=
            thead +
            tbody +
            tfoot +
            '</table> \
                </div>';
        } else {
          r += this.emptyReport(group, report.grid.columns);
        }

        i++;

        this.progress.setValue(this.partName, Math.abs(i / keys.length) * 100);

        if (keys.length === i) {
          q.resolve(r);
        }

        return true;
      }, 30);
    });

    return q.promise;
  }
}
